import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ClickableAvatar } from '@/components/ui/clickable-avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Clock, User, UserPlus, X, UserCog } from 'lucide-react';
import { format } from 'date-fns';
import type { OpenMatch } from '@/hooks/useOpenMatches';
import MiniSkillBadge from '@/components/tournament/MiniSkillBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getInitials } from '@/utils/stringUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useGroupAdmin } from '@/hooks/useGroupAdmin';
import { ReplacePlayerDialog } from '@/components/scheduled/ReplacePlayerDialog';
import type { MatchPlayerSlot } from '@/hooks/useReplaceScheduledMatchPlayer';

interface OpenMatchCardProps {
  match: OpenMatch;
  onJoinMatch: (matchId: string, team: 1 | 2, position: 1 | 2) => void;
  onLeaveMatch: (matchId: string, team: 1 | 2, position: 1 | 2) => void;
  currentUserId?: string;
  isJoining?: boolean;
  joiningSlot?: string;
  isLeaving?: boolean;
  leavingSlot?: string;
}

interface SlotPlayer {
  full_name: string;
  avatar_url?: string;
  skill_rating: number;
  id?: string;
}

interface Slot {
  player?: SlotPlayer | null;
  isOpen: boolean;
  isCurrentUser: boolean;
  team: 1 | 2;
  position: 1 | 2;
}

const OpenSlot = ({
  onJoin,
  isJoining,
  userAlreadyInMatch
}: {
  onJoin?: () => void;
  isJoining?: boolean;
  userAlreadyInMatch?: boolean;
}) => {
  const isDisabled = isJoining || userAlreadyInMatch;
  return (
    <div className="flex flex-col items-center min-w-0">
      <div className="relative mb-1">
        <Avatar
          className={`h-10 w-10 border-2 border-white shadow-sm cursor-pointer hover:scale-105 transition-transform ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={!isDisabled ? onJoin : undefined}
          title={userAlreadyInMatch ? "You're already in this match" : "Click to join this position"}
        >
          <AvatarFallback className="bg-muted text-muted-foreground">
            {isJoining ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
          </AvatarFallback>
        </Avatar>
      </div>
      <span className="text-xs text-center text-muted-foreground max-w-[65px] truncate leading-tight mb-1">
        Open
      </span>
    </div>
  );
};

const CurrentUserSlot = ({
  player,
  onLeave,
  isLeaving
}: {
  player: SlotPlayer;
  onLeave?: () => void;
  isLeaving?: boolean;
}) => {
  return (
    <div className="flex flex-col items-center min-w-0 relative">
      <div className="relative mb-1">
        <Avatar className="h-10 w-10 border-2 border-primary shadow-sm">
          <AvatarImage src={player.avatar_url} alt={player.full_name} />
          <AvatarFallback className="text-sm bg-primary/15 text-primary font-semibold">
            {getInitials(player.full_name)}
          </AvatarFallback>
        </Avatar>
        <Button
          variant="ghost"
          size="sm"
          className="absolute -top-1 -right-1 h-5 w-5 p-0 hover:bg-destructive/10 hover:text-destructive rounded-full bg-card shadow-sm"
          onClick={onLeave}
          disabled={isLeaving}
          title="Leave this position"
        >
          {isLeaving ? (
            <div className="animate-spin rounded-full h-3 w-3 border-b border-destructive" />
          ) : (
            <X className="h-3 w-3" />
          )}
        </Button>
      </div>
      <div className="text-center space-y-0.5 mb-1">
        <p className="text-xs text-center text-muted-foreground max-w-[65px] truncate leading-tight">
          {player.full_name?.split(' ')[0] || 'You'}
        </p>
        <p className="text-[10px] text-primary font-medium">You</p>
      </div>
      <MiniSkillBadge rating={player.skill_rating} size="xs" />
    </div>
  );
};

const FilledPlayerSlot = ({
  player,
  canReplace,
  onReplace
}: {
  player: SlotPlayer;
  canReplace?: boolean;
  onReplace?: () => void;
}) => {
  const firstName = player.full_name?.split(' ')[0] || 'Unknown';
  return (
    <div className="flex flex-col items-center min-w-0">
      <div className="relative mb-1">
        {player.id ? (
          <ClickableAvatar
            userId={player.id}
            avatarUrl={player.avatar_url}
            fullName={player.full_name}
            className="border-2 border-white shadow-sm"
            size="md"
          />
        ) : (
          <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
            <AvatarImage src={player.avatar_url} alt={player.full_name} />
            <AvatarFallback className="text-sm bg-primary/15 text-primary font-semibold">
              {getInitials(player.full_name)}
            </AvatarFallback>
          </Avatar>
        )}
      </div>
      <span className="text-xs text-center text-muted-foreground max-w-[65px] truncate leading-tight mb-1">
        {firstName}
      </span>
      <MiniSkillBadge rating={player.skill_rating} size="xs" />
      {canReplace && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReplace?.();
          }}
          className="mt-1 h-11 w-11 -my-2 flex items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
          aria-label={`Replace ${firstName}`}
          title="Replace player"
        >
          <UserCog className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

const PlayerSlot = ({
  slot,
  onJoin,
  onLeave,
  isJoining,
  isLeaving,
  userAlreadyInMatch,
  canReplace,
  onReplace
}: {
  slot: Slot;
  onJoin?: () => void;
  onLeave?: () => void;
  isJoining?: boolean;
  isLeaving?: boolean;
  userAlreadyInMatch?: boolean;
  canReplace?: boolean;
  onReplace?: () => void;
}) => {
  if (slot.isOpen) {
    return (
      <OpenSlot
        onJoin={onJoin}
        isJoining={isJoining}
        userAlreadyInMatch={userAlreadyInMatch}
      />
    );
  }

  if (!slot.player) {
    return (
      <div className="flex flex-col items-center min-w-0">
        <div className="h-10 w-10 rounded-full bg-muted border-2 border-white shadow-sm flex items-center justify-center mb-1">
          <span className="text-xs text-muted-foreground">?</span>
        </div>
        <span className="text-xs text-center text-muted-foreground max-w-[65px] truncate leading-tight mb-1">
          Empty
        </span>
      </div>
    );
  }

  if (slot.isCurrentUser) {
    return (
      <CurrentUserSlot
        player={slot.player}
        onLeave={onLeave}
        isLeaving={isLeaving}
      />
    );
  }

  return (
    <FilledPlayerSlot player={slot.player} canReplace={canReplace} onReplace={onReplace} />
  );
};

const renderTeamSlots = (
  slots: Slot[],
  onJoinMatch: (matchId: string, team: 1 | 2, position: 1 | 2) => void,
  onLeaveMatch: (matchId: string, team: 1 | 2, position: 1 | 2) => void,
  matchId: string,
  joiningSlot?: string,
  leavingSlot?: string,
  userAlreadyInMatch?: boolean,
  canReplacePlayers?: boolean,
  onReplaceSlot?: (slot: MatchPlayerSlot) => void
) => {
  return (
    <div className="flex items-start justify-center gap-2">
      {slots.map((slot) => (
        <PlayerSlot
          key={`team${slot.team}-pos${slot.position}`}
          slot={slot}
          onJoin={() => onJoinMatch(matchId, slot.team, slot.position)}
          onLeave={() => onLeaveMatch(matchId, slot.team, slot.position)}
          isJoining={joiningSlot === `team${slot.team}_player${slot.position}`}
          isLeaving={leavingSlot === `team${slot.team}_player${slot.position}`}
          userAlreadyInMatch={userAlreadyInMatch}
          canReplace={canReplacePlayers}
          onReplace={() =>
            onReplaceSlot?.(`team${slot.team}_player${slot.position}` as MatchPlayerSlot)
          }
        />
      ))}
    </div>
  );
};

export const OpenMatchCard = ({
  match,
  onJoinMatch,
  onLeaveMatch,
  currentUserId,
  isJoining,
  joiningSlot,
  isLeaving,
  leavingSlot
}: OpenMatchCardProps) => {
  const { user } = useAuth();
  const { isAdmin: isGroupAdmin } = useGroupAdmin(match.group_id);
  const [replaceSlot, setReplaceSlot] = useState<MatchPlayerSlot | null>(null);

  const matchDate = new Date(match.match_date);
  const isUpcoming = matchDate.getTime() > Date.now();
  const canReplacePlayers =
    !!user && isUpcoming && (isGroupAdmin || match.published_by === user.id);
  const userAlreadyInMatch = currentUserId && (
    match.team1_player1 === currentUserId ||
    match.team1_player2 === currentUserId ||
    match.team2_player1 === currentUserId ||
    match.team2_player2 === currentUserId
  );

  const team1Slots: Slot[] = [
    {
      player: match.team1_player1_profile,
      isOpen: !match.team1_player1,
      isCurrentUser: match.team1_player1 === currentUserId,
      team: 1,
      position: 1
    },
    {
      player: match.team1_player2_profile,
      isOpen: !match.team1_player2,
      isCurrentUser: match.team1_player2 === currentUserId,
      team: 1,
      position: 2
    }
  ];

  const team2Slots: Slot[] = [
    {
      player: match.team2_player1_profile,
      isOpen: !match.team2_player1,
      isCurrentUser: match.team2_player1 === currentUserId,
      team: 2,
      position: 1
    },
    {
      player: match.team2_player2_profile,
      isOpen: !match.team2_player2,
      isCurrentUser: match.team2_player2 === currentUserId,
      team: 2,
      position: 2
    }
  ];

  return (
    <>
    <Card className="hover:shadow-md transition-all duration-200 h-[320px]">
      <CardContent className="p-0 h-full flex flex-col">
        <div className="p-4 h-full flex flex-col">
          {/* Badges */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex-grow flex items-center gap-2 flex-wrap">
              {match.available_spots === 0 && (
                <Badge variant="secondary" className="text-xs">
                  Match Full
                </Badge>
              )}
              <Badge className="text-xs bg-primary hover:bg-primary/90">
                Open
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {match.available_spots} {match.available_spots === 1 ? 'spot' : 'spots'} available
              </Badge>
            </div>
          </div>

          {/* Teams */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              {renderTeamSlots(
                team1Slots,
                onJoinMatch,
                onLeaveMatch,
                match.id,
                joiningSlot,
                leavingSlot,
                !!userAlreadyInMatch,
                canReplacePlayers,
                setReplaceSlot
              )}
            </div>

            <div className="flex-shrink-0 mx-3 self-center">
              <div className="w-8 h-8 rounded-full bg-muted border border-muted-foreground/20 flex items-center justify-center">
                <span className="text-xs font-bold text-muted-foreground">VS</span>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              {renderTeamSlots(
                team2Slots,
                onJoinMatch,
                onLeaveMatch,
                match.id,
                joiningSlot,
                leavingSlot,
                !!userAlreadyInMatch,
                canReplacePlayers,
                setReplaceSlot
              )}
            </div>
          </div>

          {/* Match Details */}
          <div className="flex flex-col space-y-1 text-sm text-muted-foreground mb-3">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">{format(matchDate, 'MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">{format(matchDate, 'h:mm a')}</span>
            </div>
            {match.club && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate max-w-[220px]">{match.club.club_name}</span>
              </div>
            )}
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1 flex-shrink-0" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground truncate max-w-[220px]">
                      Created by: {match.publisher_profile?.full_name || 'Unknown Player'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Match creator: {match.publisher_profile?.full_name || 'Unknown Player'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          <div className="flex-1" />
        </div>
      </CardContent>
    </Card>

    <ReplacePlayerDialog
      open={replaceSlot !== null}
      onOpenChange={(o) => { if (!o) setReplaceSlot(null); }}
      match={match as any}
      slot={replaceSlot}
    />
    </>
  );
};
